import { DomainError } from '@/shared/types/errors';

// Subscription types for family token-based billing

export type SubscriptionStatus = 'active' | 'suspended' | 'canceled';

export interface Subscription {
  id: string;
  familyId: string;
  status: SubscriptionStatus;
  balance: number; // Integer token count
  createdAt: string; // ISO timestamp
  updatedAt: string; // ISO timestamp
  _etag?: string; // ETag for optimistic concurrency
  stripeCustomerId?: string; // Stripe Customer ID for payment processing
}

export type TransactionType =
  | 'initial_allocation'
  | 'admin_credit'
  | 'admin_debit'
  | 'feature_consumption'
  | 'promotional_credit'
  | 'correction'
  | 'pre_authorization'
  | 'usage'  // Actual token usage (replaces settlement)
  | 'additional_charge'  // Additional charge when actual > reserved
  | 'purchase'
  | 'refund';

export type ActorRole = 'admin' | 'adult' | 'system';

export interface SubscriptionTransaction {
  id: string;
  familyId: string;
  amount: number; // Positive for credit, negative for debit
  type: TransactionType;
  reason: string;
  actorUserId: string;
  actorRole: ActorRole;
  resultingBalance: number; // Balance after this transaction
  createdAt: string; // ISO timestamp
  metadata?: {
    storyId?: string;
    storyTitle?: string;
    blockId?: string;
    blockNumber?: number;
    action?: TokenAction;
    reservationId?: string;
    reservedAmount?: number;
    actualCost?: number; // Наши токены (внутренняя валюта)
    operationType?: string;

    // External service usage tracking (for analytics)
    llmCharactersUsed?: number;      // Символов отправлено в LLM (OpenAI/Azure)
    llmTokensConsumed?: number;      // Токены потребленные LLM API (от провайдера)
    llmModel?: string;               // Модель LLM (gpt-4, gpt-3.5-turbo, etc.)

    ttsCharactersUsed?: number;      // Символов отправлено в TTS (ElevenLabs)
    ttsCreditsConsumed?: number;     // Кредиты потребленные TTS API (от ElevenLabs)
    ttsModel?: string;               // Модель TTS (eleven_multilingual_v2, etc.)
  };
}

export interface BillingPolicy {
  id: string; // Always 'subscription_default_allocation'
  defaultInitialTokens: number; // Integer
  updatedBy: string; // Admin user ID
  updatedAt: string; // ISO timestamp
}

// Pricing policy for AI token usage
// NOTE: USD costs (for margin calculation) are stored in AIProviderConfig
// Use AIProviderConfigService.getLLMPricing() / getTTSPricing() for USD costs
export interface PricingPolicy {
  id: string; // Always 'pricing_policy'
  isActive: boolean; // Whether this policy is currently active

  // === Тарифы для пользователей (наши токены) ===
  rates: {
    llm: {
      tokensPer10K: number; // Наши токены за 10K LLM токенов
    };
    tts: {
      tokensPerK: number; // Наши токены за 1K TTS символов
    };
    assembly: {
      fixed: number; // Фикс за сборку аудио
    };
    imagen?: {
      tokensPerImage: number; // Наши токены за 1 изображение (default: 50)
    };
  };

  // Legacy fields (for backward compatibility)
  textPerK?: number; // DEPRECATED: use rates.llm.tokensPer10K
  audioPerK?: number; // DEPRECATED: use rates.tts.tokensPerK
  assemblyFixed?: number; // DEPRECATED: use rates.assembly.fixed
  imagenPerImage?: number; // DEPRECATED: use rates.imagen.tokensPerImage

  // Precharge reservations (tokens reserved upfront, refunded if not used)
  storyReservation: number; // Tokens reserved for story creation (default: 20)
  blockTextReservation: number; // Tokens reserved for text block operation (default: 1)
  blockAudioReservation: number; // Tokens reserved for audio block operation (default: 1)
  storyAnalysisReservation?: number; // Tokens for story analysis in book creation (default: 15)

  // Metadata
  effectiveAt: string; // ISO timestamp when this policy becomes active
  version: number; // Version number for tracking
  updatedBy: string; // Admin user ID
  updatedAt: string; // ISO timestamp
  reason?: string; // Optional reason for the change
}

// Action types for token charging
export type TokenAction =
  | 'story_generation'
  | 'quick_story_generation'
  | 'block_regeneration'
  | 'audio_per_block'
  | 'audio_batch_missing'
  | 'final_assembly'
  | 'character_enhancement'
  | 'character_creation'
  | 'prompt_enhancement'
  | 'page_generation'
  | 'cover_generation'
  | 'refund'
  | 'refund_settlement'
  | 'additional_charge'
  | 'settlement_exact';

// API request/response types
export interface CreditDebitParams {
  amount: number;
  reason: string;
  actorUserId: string;
  actorRole: ActorRole;
}

export interface TransactionFilters {
  familyId?: string;
  storyId?: string;
  stripeSessionId?: string;
  startDate?: string;
  endDate?: string;
  type?: TransactionType;
  actorUserId?: string;
  limit?: number;
  continuationToken?: string;
}

// Error codes
export const SUBSCRIPTION_ERRORS = {
  INSUFFICIENT_BALANCE: 'INSUFFICIENT_BALANCE',
  INVALID_AMOUNT: 'INVALID_AMOUNT',
  SUBSCRIPTION_NOT_FOUND: 'SUBSCRIPTION_NOT_FOUND',
  ETAG_CONFLICT: 'ETAG_CONFLICT',
  POLICY_NOT_FOUND: 'POLICY_NOT_FOUND',
} as const;

export type SubscriptionErrorCode = typeof SUBSCRIPTION_ERRORS[keyof typeof SUBSCRIPTION_ERRORS];

export class SubscriptionError extends DomainError {
  constructor(
    public code: SubscriptionErrorCode,
    message: string
  ) {
    super(message);
    this.name = 'SubscriptionError';
  }
}
